import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';
import parse from 'date-fns/parse';
import { BasketActions, BasketActionTypes } from '../../basket.types';
import { AppState, ErrorAction } from '../../../root.types';
import { userHelper, deliveryHelper, errorToBasketErrorModel } from 'helpers';
import { api } from 'repositories/api';
import { loadBasketSaga } from '../loadBasket';
import { ReservationInfoModel } from 'models';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';

const MAX_RESERVATION_DIFFERENCE = 0.001;

export interface ValidateBasketReservationsAction extends Action<BasketActionTypes.VALIDATE_BASKET_RESERVATIONS> {
    isBasketPage?: boolean;
}

export interface ValidateBasketReservationsActionStart extends Action<BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_START> {
}

export interface ValidateBasketReservationsActionSuccess extends Action<BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_SUCCESS> {
}

export interface ValidateBasketReservationsActionError extends ErrorAction<BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_ERROR> {
}

export function* validateBasketReservationsSaga({ isBasketPage }: ValidateBasketReservationsAction): BasketSagaReturnType {
    yield put<BasketActions>({
        type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_START,
    });

    try {
        let state: AppState = yield select();
        const basket = state.basketState.basket!;
        const basketState = state.basketState;

        if (isBasketPage && !basket.deliveryAddress?.postcode) {
            yield put<BasketActions>({
                type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_SUCCESS
            });

            return true;
        }

        let hasBasketChanged = false;

        if (!isBasketPage) {
            const paymentDetails = state.paymentState.paymentDetails;

            hasBasketChanged = floatsAreNotEqual(basket.totalPriceWithTax.value, paymentDetails.total);
        }

        const reservationInfo = yield api.reservation.getReservationInfo(basket.guid);
        const minutesLeft = getMinutesLeft(reservationInfo);

        if (minutesLeft <= 0) {
            // calculateDeliveryCost updates reservation if it is possible
            // it also removes expired items (in background) from the basket
            // without this call we are having expired items in the basket for an unlimited time
            const postcode = basket.deliveryAddress!.postcode;
            yield deliveryHelper.calculateDeliveryCost(postcode, postcode, userHelper.getCurrentUserName(), basket.guid);

            yield callBasketSaga(loadBasketSaga);
            state = yield select();
            const newBasket = state.basketState.basket!;

            hasBasketChanged = hasBasketChanged || newBasket.totalItems !== basket.totalItems && basketState.basketLoadingItems.length === 0;
        }

        yield put<BasketActions>({
            type: BasketActionTypes.SET_SHOW_RESERVATION_WARNING,
            shouldShowReservationWarning: hasBasketChanged
        });

        yield put<BasketActions>({
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<BasketActions>({
            type: BasketActionTypes.VALIDATE_BASKET_RESERVATIONS_ERROR,
            error: errorToBasketErrorModel(error, 'validateBasketReservationsSaga')
        });

        return false;
    }
}

// from old code
function getMinutesLeft(reservationInfo: ReservationInfoModel): number {
    if (!reservationInfo.reservationValidUntil) {
        return 0;
    }

    const expiry = new Date(reservationInfo.reservationValidUntil).getTime();
    const convertedDate = getCurrentGBDate();

    if (expiry < convertedDate) {
        return 0;
    }

    return Math.round((expiry - convertedDate) / 1000 / 60);
}

function getCurrentGBDate(): number {
    const GBDate = new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' });

    return parse(
        GBDate,
        'dd/MM/yyyy, HH:mm:ss',
        new Date()
    ).getTime();
}

// from old code
function floatsAreNotEqual(n1: number, n2: number) {
    return Math.abs(n1 - n2) > MAX_RESERVATION_DIFFERENCE;
}